/**
 * React hook for managing push notification subscriptions
 *
 * Automatically subscribes Admin users to push notifications
 * when they log in (if permission is granted).
 * Users can allow permissions manually in the parametres page.
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchVapidPublicKey,
  isPushNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  refreshPushSubscription,
  PushNotificationError,
  flushPendingPushReceipts,
} from "@/lib/push-notifications";

interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  requestPermission: () => Promise<NotificationPermission>;
  refreshStatus: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [isSupported] = useState(isPushNotificationSupported());
  const [permission, setPermission] = useState<NotificationPermission>(
    getNotificationPermission(),
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to check and sync subscription status
  const refreshStatus = useCallback(async () => {
    if (!isSupported) {
      setIsLoading(false);
      return;
    }

    try {
      await fetchVapidPublicKey();
      const status = await refreshPushSubscription();

      setIsSubscribed(status.isSubscribed);
    } catch (err) {
      console.error("[Push] Error checking subscription status:", err);
      if (err instanceof PushNotificationError) {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Check subscription status on mount
  useEffect(() => {
    void flushPendingPushReceipts();
    refreshStatus();
  }, [refreshStatus, userId]);

  // The user may change the permission in browser/OS settings while the app
  // is in the background: re-read it and re-sync when the tab becomes visible.
  useEffect(() => {
    if (!isSupported || typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      void flushPendingPushReceipts();
      const current = getNotificationPermission();
      setPermission((previous) => (previous === current ? previous : current));
      void refreshStatus();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    const handleOnline = () => {
      void flushPendingPushReceipts();
      void refreshStatus();
    };
    window.addEventListener("online", handleOnline);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [isSupported, refreshStatus]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError("Notifications push non supportées");
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission if not granted
      if (permission !== "granted") {
        const newPermission = await requestNotificationPermission();
        setPermission(newPermission);

        if (newPermission !== "granted") {
          setError(
            newPermission === "denied"
              ? "Notifications bloquées. Autorisez-les dans les paramètres du navigateur."
              : "Permission refusée",
          );
          return false;
        }
      }

      await subscribeToPushNotifications();
      setIsSubscribed(true);
      await refreshStatus();

      return true;
    } catch (err) {
      console.error("[Push] Subscribe error:", err);

      // Build error message
      let errorMsg = "Erreur lors de l'activation des notifications";
      if (err instanceof PushNotificationError) {
        errorMsg = err.message;
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);

      await refreshStatus();

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, permission, refreshStatus]);

  useEffect(() => {
    if (!isSupported) return;
    const handleSubscriptionChange = (event: MessageEvent) => {
      if (event.data?.type !== "ODILON_PUSH_SUBSCRIPTION_CHANGED") return;
      void refreshStatus();
    };
    navigator.serviceWorker.addEventListener("message", handleSubscriptionChange);
    return () => navigator.serviceWorker.removeEventListener("message", handleSubscriptionChange);
  }, [isSupported, refreshStatus]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const success = await unsubscribeFromPushNotifications();

      // Re-verify status after unsubscribing
      await refreshStatus();

      if (success) {
        setIsSubscribed(false);
      } else {
        console.error("[Push] Unsubscribe failed");
        setError("Échec de la désactivation des notifications");
      }

      return success;
    } catch (err) {
      console.error("[Push] Unsubscribe error:", err);
      setError("Erreur lors de la désactivation des notifications");

      // Refresh status to ensure UI is accurate
      await refreshStatus();

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, refreshStatus]);

  const handleRequestPermission = async (): Promise<NotificationPermission> => {
    setError(null);
    const newPermission = await requestNotificationPermission();
    setPermission(newPermission);
    return newPermission;
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    requestPermission: handleRequestPermission,
    refreshStatus,
  };
}
